import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuthContext } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SalePageNavbar from '../../components/SalePageNavbar';
import { ArrowLeft, Wallet, Receipt, Download, FileText, Loader } from 'lucide-react';

const FONT = '"IBM Plex Sans Thai", system-ui, sans-serif';
const HEAD = "'Anuphan', sans-serif";
const INK = '#0E3A40';

interface Payment {
  id: string;
  amount: number;
  plan: string;
  status: 'pending' | 'verified' | 'rejected';
  createdAt: any;
  paymentMethod?: string;
  slipUrl?: string;
  note?: string;
  type?: string;
}

type Norm = 'success' | 'pending' | 'failed';
const normalize = (s: string): Norm => (s === 'verified' ? 'success' : s === 'rejected' ? 'failed' : 'pending');

function statusMeta(n: Norm) {
  if (n === 'success') return { label: 'สำเร็จ', bg: 'rgba(0,180,161,0.12)', color: '#0E7C86' };
  if (n === 'pending') return { label: 'รอดำเนินการ', bg: 'rgba(58,147,185,0.12)', color: '#276E8A' };
  return { label: 'ล้มเหลว', bg: 'rgba(255,122,69,0.12)', color: '#B3552A' };
}

function getPlanName(plan: string, type?: string) {
  if (type === 'trial_start') return 'เริ่มทดลองใช้ฟรี 7 วัน';
  switch (plan) {
    case 'basic': return 'Basic รายเดือน';
    case 'premium': return 'Premium รายเดือน';
    case 'start': return 'Start (ทดลองใช้ฟรี)';
    case 'pro_plus_yearly': return 'Pro+ Yearly (ฟรี)';
    case 'free': return 'FREE (ทดลองใช้ฟรี)';
    default: return plan;
  }
}

function formatDate(timestamp: any) {
  if (!timestamp) return '-';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat('th-TH', { year: 'numeric', month: 'long', day: 'numeric' }).format(date);
}
function yearOf(timestamp: any): number | null {
  if (!timestamp) return null;
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.getFullYear();
}

export default function SalePagePaymentHistory() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | Norm>('all');

  useEffect(() => {
    const fetchPayments = async () => {
      if (!user?.uid) return;
      try {
        const q = query(
          collection(db, 'salePagePaymentNotifications'),
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        const data: Payment[] = [];
        snap.forEach((d) => data.push({ id: d.id, ...d.data() } as Payment));
        setPayments(data);
      } catch (error) {
        console.error('Error fetching payments:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPayments();
  }, [user]);

  const thisYear = new Date().getFullYear();
  const yearTotal = payments
    .filter((p) => normalize(p.status) === 'success' && yearOf(p.createdAt) === thisYear)
    .reduce((s, p) => s + (p.amount || 0), 0);
  const successCount = payments.filter((p) => normalize(p.status) === 'success').length;
  const filtered = filter === 'all' ? payments : payments.filter((p) => normalize(p.status) === filter);

  const filterBtn = (active: boolean): React.CSSProperties => ({
    fontFamily: HEAD, fontSize: 13, fontWeight: 500, padding: '8px 15px', borderRadius: 11,
    border: 0, cursor: 'pointer',
    background: active ? '#0E3A40' : 'transparent', color: active ? '#fff' : '#5B7D80',
  });

  const filters: { key: 'all' | Norm; label: string }[] = [
    { key: 'all', label: 'ทั้งหมด' },
    { key: 'success', label: 'สำเร็จ' },
    { key: 'pending', label: 'รอดำเนินการ' },
    { key: 'failed', label: 'ล้มเหลว' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#F3F8F8', color: INK, fontFamily: FONT }}>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Anuphan:wght@400;500;600;700&family=IBM+Plex+Sans+Thai:wght@400;500;600&display=swap" rel="stylesheet" />
      </Helmet>

      <SalePageNavbar />

      <main style={{ maxWidth: 900, margin: '0 auto', padding: 'clamp(22px,3vw,34px) clamp(16px,3vw,32px) clamp(48px,6vw,80px)', display: 'flex', flexDirection: 'column', gap: 'clamp(18px,2.4vw,24px)' }}>
        {/* Title + back */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button type="button" onClick={() => navigate('/salepage/renew')} style={{ width: 38, height: 38, borderRadius: 12, background: '#fff', border: '1px solid rgba(14,58,64,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto', cursor: 'pointer' }}>
            <ArrowLeft style={{ width: 17, height: 17, color: INK }} />
          </button>
          <div>
            <h1 style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 'clamp(22px,2.4vw,27px)', margin: '0 0 3px', letterSpacing: '-0.02em' }}>ประวัติการชำระเงิน</h1>
            <p style={{ fontSize: 14, color: '#5B7D80', margin: 0 }}>ดูประวัติการชำระเงิน Salepage ทั้งหมดของคุณ</p>
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '60px 0' }}>
            <Loader style={{ width: 40, height: 40, color: '#00B4A1' }} className="animate-spin" />
            <p style={{ color: '#5B7D80' }}>กำลังโหลด...</p>
          </div>
        ) : payments.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 24, padding: 'clamp(50px,7vw,72px) clamp(24px,4vw,40px)', border: '1px solid rgba(14,58,64,0.06)', boxShadow: '0 30px 60px -48px rgba(14,58,64,0.35)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14 }}>
            <span style={{ width: 64, height: 64, borderRadius: 999, background: 'rgba(58,147,185,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText style={{ width: 28, height: 28, color: '#3A93B9' }} />
            </span>
            <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 19 }}>ยังไม่มีประวัติการชำระเงิน</div>
            <p style={{ fontSize: 14, color: '#5B7D80', margin: 0, maxWidth: '28em' }}>เมื่อคุณชำระเงินสำหรับ Salepage ประวัติจะแสดงที่นี่</p>
          </div>
        ) : (
          <>
            {/* Summary */}
            <div style={{ background: 'linear-gradient(165deg, rgba(0,180,161,0.08), rgba(58,147,185,0.05))', border: '1px solid rgba(0,180,161,0.25)', borderRadius: 22, padding: 'clamp(18px,2.4vw,24px) clamp(20px,2.8vw,26px)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(0,180,161,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Wallet style={{ width: 19, height: 19, color: '#00B4A1' }} />
                </span>
                <div>
                  <div style={{ fontSize: 12.5, color: '#5B7D80' }}>ยอดรวมที่ชำระในปีนี้</div>
                  <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 24 }}>฿{yearTotal.toLocaleString()}</div>
                </div>
              </div>
              <span style={{ fontSize: 12.5, color: '#5B7D80' }}>{successCount} รายการสำเร็จ จากทั้งหมด {payments.length} รายการ</span>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 6, background: '#fff', borderRadius: 16, padding: 5, border: '1px solid rgba(14,58,64,0.06)', width: 'fit-content', flexWrap: 'wrap' }}>
              {filters.map((f) => (
                <button key={f.key} type="button" onClick={() => setFilter(f.key)} style={filterBtn(filter === f.key)}>{f.label}</button>
              ))}
            </div>

            {/* List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filtered.length === 0 ? (
                <div style={{ textAlign: 'center', fontSize: 13.5, color: '#5B7D80', padding: '24px 0' }}>ไม่มีรายการในหมวดนี้</div>
              ) : (
                filtered.map((p) => {
                  const m = statusMeta(normalize(p.status));
                  return (
                    <div key={p.id} style={{ background: '#fff', borderRadius: 18, padding: 'clamp(16px,2vw,20px)', border: '1px solid rgba(14,58,64,0.06)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <span style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(0,180,161,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                          <Receipt style={{ width: 18, height: 18, color: '#00B4A1' }} />
                        </span>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 15 }}>{getPlanName(p.plan, p.type)}</span>
                            <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: m.bg, color: m.color }}>{m.label}</span>
                          </div>
                          <div style={{ fontSize: 12.5, color: '#5B7D80' }}>{formatDate(p.createdAt)}{p.note ? ` · ${p.note}` : ''}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 16, color: p.amount === 0 ? '#B45309' : INK }}>{p.amount === 0 ? 'ฟรี' : `฿${(p.amount || 0).toLocaleString()}`}</span>
                        {p.slipUrl && (
                          <a href={p.slipUrl} target="_blank" rel="noopener noreferrer" title="ดูสลิป" style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid rgba(14,58,64,0.1)', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Download style={{ width: 15, height: 15, color: '#5B7D80' }} />
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
